/**
 * Critical-path resolver — the 8-way matcher lifted verbatim from the original
 * CytoscapeRenderer.tsx so BOTH the production Cytoscape renderer and the new
 * SVG `ObsidianGraph` resolve critical paths identically.
 *
 * Operates on plain element data (no Cytoscape `Core`), returning the matched
 * edge ids and node ids. `applyCriticalPath` (renderer/criticalPath.ts) is a thin
 * Cytoscape adapter over this; ObsidianGraph consumes the sets directly.
 *
 * Resolution strategies (in order):
 *   1. Edge id match (exact, or `${src}-${label}-${tgt}` combo).
 *   2. Ordered NODE id/label sequence → edges between consecutive resolved pairs.
 *      Each entry is resolved by: exact id → exact label → stripped-prefix id →
 *      bidirectional substring / token-overlap fuzzy match.
 */

import type { GraphElementDefinition } from './types';

const TYPE_PREFIXES =
  /^(host|ip|user|alert|process|file|domain|hash|app|role|sa|svc|ec2|lambda|k8s|asn|internet|org|geo|vuln|threat|campaign|cert|secret|sg|s3|rds|db|mitre|policy|email|container)-/;

export interface CriticalPathResult {
  /** Edge ids on the critical path. */
  edgeIds: Set<string>;
  /** Node ids on the critical path. */
  nodeIds: Set<string>;
  /** How many edges / consecutive node pairs were matched (>0 ⇒ dim the rest). */
  matched: number;
}

interface EdgeLike {
  id: string;
  source: string;
  target: string;
  label: string;
}
interface NodeLike {
  id: string;
  label: string;
}

/**
 * Resolve a critical path against a set of graph elements.
 *
 * @param elements The full element list (nodes + edges), Cytoscape-shaped.
 * @param criticalPath Edge ids, or an ordered sequence of node ids/labels.
 */
export function resolveCriticalPath(
  elements: GraphElementDefinition[],
  criticalPath: string[] | undefined,
): CriticalPathResult {
  const edgeIds = new Set<string>();
  const nodeIds = new Set<string>();
  if (!criticalPath || criticalPath.length === 0) {
    return { edgeIds, nodeIds, matched: 0 };
  }

  const nodes: NodeLike[] = [];
  const edges: EdgeLike[] = [];
  for (const el of elements) {
    const isEdge = el.group === 'edges' || (!el.group && !!el.data.source);
    if (isEdge) {
      const source = (el.data.source as string) ?? '';
      const target = (el.data.target as string) ?? '';
      edges.push({
        // Fall back to the SAME synthetic id the SVG renderer derives
        // (`${source}-${target}`) when an edge has no explicit data.id, so the
        // resolved edgeIds match what ObsidianGraph looks up. (A bare '' here
        // never matched the renderer's fallback → id-less critical edges were
        // silently not highlighted.)
        id: (el.data.id as string) ?? `${source}-${target}`,
        source,
        target,
        label: (el.data.label as string) ?? '',
      });
    } else {
      nodes.push({ id: (el.data.id as string) ?? '', label: (el.data.label as string) ?? '' });
    }
  }

  let matched = 0;
  const pathEdgeIds = new Set(criticalPath);

  // Strategy 1: edge id match (exact or source-label-target combo).
  for (const e of edges) {
    const label = e.label.replace(/\s/g, '_');
    if (pathEdgeIds.has(e.id) || pathEdgeIds.has(`${e.source}-${label}-${e.target}`)) {
      edgeIds.add(e.id);
      nodeIds.add(e.source);
      nodeIds.add(e.target);
      matched++;
    }
  }

  // Strategy 2: ordered node id/label sequence.
  if (matched === 0) {
    const idSet = new Set<string>();
    const labelToId = new Map<string, string>();
    const strippedIdToId = new Map<string, string>();
    for (const n of nodes) {
      idSet.add(n.id);
      const lbl = n.label.toLowerCase();
      if (lbl) labelToId.set(lbl, n.id);
      const stripped = n.id.replace(TYPE_PREFIXES, '');
      if (stripped !== n.id) strippedIdToId.set(stripped.toLowerCase(), n.id);
    }

    const resolvedIds: string[] = [];
    for (const entry of criticalPath) {
      // 1. Exact id
      if (idSet.has(entry)) { resolvedIds.push(entry); continue; }
      // 2. Exact label (case-insensitive)
      const byLabel = labelToId.get(entry.toLowerCase());
      if (byLabel) { resolvedIds.push(byLabel); continue; }
      // 3. Stripped prefix
      const entryStripped = entry.replace(TYPE_PREFIXES, '').toLowerCase();
      const byStripped = strippedIdToId.get(entryStripped);
      if (byStripped) { resolvedIds.push(byStripped); continue; }
      // 4. Fuzzy: bidirectional substring + token overlap
      let found: string | null = null;
      let bestScore = 0;
      const eLower = entry.toLowerCase();
      // Entries shorter than 3 chars are too ambiguous to fuzzy-match — a bare
      // "db"/"ip" would substring-match unrelated nodes and mis-dim the graph.
      // Exact id/label/stripped matches above already handled the safe cases.
      if (eLower.length < 3) continue;
      for (const n of nodes) {
        const nidLower = n.id.toLowerCase();
        const lbl = n.label.toLowerCase();
        for (const c of [nidLower, lbl]) {
          if (!c) continue;
          if (c.includes(eLower) || eLower.includes(c)) {
            const score = Math.min(c.length, eLower.length);
            if (score > bestScore) { bestScore = score; found = n.id; }
          }
          const entryTokens = eLower.split(/[^a-z0-9]+/).filter((t) => t.length >= 3);
          const cTokens = c.split(/[^a-z0-9]+/).filter((t) => t.length >= 3);
          const overlap = entryTokens.filter((t) => cTokens.some((ct) => ct.includes(t) || t.includes(ct)));
          if (overlap.length > 0 && overlap.length >= Math.min(entryTokens.length, cTokens.length)) {
            const score = overlap.join('').length;
            if (score > bestScore) { bestScore = score; found = n.id; }
          }
        }
      }
      if (found) resolvedIds.push(found);
    }

    if (resolvedIds.length >= 2) {
      for (const nid of resolvedIds) nodeIds.add(nid);
      for (let i = 0; i < resolvedIds.length - 1; i++) {
        const src = resolvedIds[i]!;
        const tgt = resolvedIds[i + 1]!;
        for (const e of edges) {
          if ((e.source === src && e.target === tgt) || (e.source === tgt && e.target === src)) {
            edgeIds.add(e.id);
            matched++;
          }
        }
      }
      // Some consecutive pairs had no direct edge but we still want to dim the rest.
      if (matched === 0 && nodeIds.size >= 2) matched = 1;
    }
  }

  return { edgeIds, nodeIds, matched };
}
